import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';

const AuthContext = createContext(null);
const API_BASE = process.env.REACT_APP_BACKEND_URL || '';
const TOKEN_KEY = 'trove_helix_token';
const USER_KEY = 'trove_helix_user';

// Guard: warn if a future build accidentally bakes in a cross-origin
// backend URL again (e.g. the preview URL leaking into a prod bundle).
// Same-origin (empty API_BASE) is the supported configuration.
if (typeof window !== 'undefined' && API_BASE) {
  try {
    const baseOrigin = new URL(API_BASE).origin;
    if (baseOrigin !== window.location.origin) {
      // eslint-disable-next-line no-console
      console.warn(
        `[trove helix] WARNING: REACT_APP_BACKEND_URL (${baseOrigin}) ` +
        `differs from window origin (${window.location.origin}). ` +
        `HttpOnly auth cookies will fail across origins. ` +
        `Rebuild admin with REACT_APP_BACKEND_URL="" for same-origin API calls.`
      );
    }
  } catch { /* malformed URL — ignore */ }
}

function getStoredToken() {
  return localStorage.getItem(TOKEN_KEY);
}

function getStoredUser() {
  try {
    const u = localStorage.getItem(USER_KEY);
    return u ? JSON.parse(u) : null;
  } catch { return null; }
}

function authHeaders() {
  const token = getStoredToken();
  return token ? { Authorization: `Bearer ${token}` } : {};
}

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);      // null = checking, false = not auth'd
  const [loading, setLoading] = useState(true);

  const checkAuth = useCallback(async () => {
    // CRITICAL: If returning from OAuth callback, skip the /me check.
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    if (window.location.hash?.includes('session_id=')) {
      setLoading(false);
      return;
    }

    const token = getStoredToken();
    if (!token) {
      setUser(false);
      setLoading(false);
      return;
    }

    try {
      const res = await fetch(`${API_BASE}/api/admin/auth/me`, {
        headers: authHeaders(),
      });
      if (res.ok) {
        const data = await res.json();
        setUser(data);
        localStorage.setItem(USER_KEY, JSON.stringify(data));
      } else {
        localStorage.removeItem(TOKEN_KEY);
        localStorage.removeItem(USER_KEY);
        setUser(false);
      }
    } catch {
      setUser(false);
    }
    setLoading(false);
  }, []);

  useEffect(() => {
    checkAuth();
  }, [checkAuth]);

  const login = async (email, password) => {
    const res = await fetch(`${API_BASE}/api/admin/auth/login`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ email, password }),
    });
    if (!res.ok) {
      const err = await res.json();
      throw new Error(typeof err.detail === 'string' ? err.detail : 'Login failed');
    }
    const data = await res.json();
    localStorage.setItem(TOKEN_KEY, data.access_token);
    localStorage.setItem(USER_KEY, JSON.stringify(data));
    setUser(data);
    return data;
  };

  const loginWithGoogle = async (sessionId) => {
    const res = await fetch(`${API_BASE}/api/admin/auth/google-session`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ session_id: sessionId }),
    });
    if (!res.ok) throw new Error('Google auth failed');
    const data = await res.json();
    localStorage.setItem(TOKEN_KEY, data.access_token);
    localStorage.setItem(USER_KEY, JSON.stringify(data));
    setUser(data);
    return data;
  };

  const logout = async () => {
    try {
      await fetch(`${API_BASE}/api/admin/auth/logout`, {
        method: 'POST',
        headers: authHeaders(),
      });
    } catch {}
    localStorage.removeItem(TOKEN_KEY);
    localStorage.removeItem(USER_KEY);
    setUser(false);
  };

  const trackPageView = (page, dwellSeconds) => {
    fetch(`${API_BASE}/api/admin/auth/track`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', ...authHeaders() },
      body: JSON.stringify({ page, dwell_seconds: dwellSeconds }),
    }).catch(() => {});
  };

  return (
    <AuthContext.Provider value={{ user, loading, login, loginWithGoogle, logout, trackPageView }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  return useContext(AuthContext);
}
